import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight, 
  Zap, 
  Calendar, 
  Phone, 
  MessageCircle,
  Star,
  CheckCircle2,
  Clock,
  DollarSign,
  Shield,
  Heart,
  Sparkles
} from 'lucide-react';

const LiveStreamCTA = () => {
  const urgencyFactors = [
    {
      icon: Calendar,
      title: "Recital Season is Here",
      description: "Don't let families miss another moment. Get streaming before your next event.",
      timeframe: "Setup in 10 minutes"
    },
    {
      icon: DollarSign,
      title: "Revenue Opportunity",
      description: "Each event you don't stream is $1,000-$5,000 in missed revenue.",
      timeframe: "ROI from first event"
    },
    {
      icon: Heart,
      title: "Family Connections",
      description: "Deployed parents, distant grandparents - they're waiting to watch.",
      timeframe: "Priceless memories"
    }
  ];

  const guarantees = [
    "✅ Money-back guarantee if not satisfied",
    "✅ First event success guarantee", 
    "✅ 24/7 technical support during events",
    "✅ No setup fees or hidden costs",
    "✅ Cancel anytime, no contracts"
  ];

  const actionOptions = [
    {
      title: "Start Streaming Now",
      subtitle: "Get setup and stream your first event",
      icon: Zap,
      action: "immediate",
      buttonText: "Start Free Trial",
      featured: true,
      benefits: ["Immediate access", "10-minute setup", "First event free"]
    },
    {
      title: "Schedule Live Demo",
      subtitle: "See HiveShare Live in action",
      icon: Calendar,
      action: "demo",
      buttonText: "Book Demo",
      featured: false,
      benefits: ["Personal walkthrough", "Custom pricing", "Q&A session"]
    },
    {
      title: "Talk to Expert",
      subtitle: "Speak with streaming specialist",
      icon: Phone,
      action: "call",
      buttonText: "Call Now",
      featured: false,
      benefits: ["Immediate answers", "Custom solutions", "Volume discounts"]
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Urgency Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <Badge className="bg-gradient-to-r from-red-500 to-orange-500 text-white mb-6 px-6 py-2 text-lg animate-pulse">
            <Sparkles className="h-5 w-5 mr-2" />
            Don't Wait - Families Are Missing Out!
          </Badge>
          
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Every Event Without Streaming is
            <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent"> Lost Revenue</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-12">
            While you're thinking about it, your competitors are already streaming. Deployed military families, distant grandparents, and traveling relatives are missing precious moments that can never be recaptured.
          </p>

          {/* Urgency Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {urgencyFactors.map((factor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-orange-100 hover:border-orange-200">
                  <CardContent className="p-8 text-center">
                    <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl p-4 w-fit mx-auto mb-6">
                      <factor.icon className="h-8 w-8 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{factor.title}</h3>
                    <p className="text-gray-600 mb-4">{factor.description}</p>
                    <Badge className="bg-orange-100 text-orange-700">
                      <Clock className="h-4 w-4 mr-1" />
                      {factor.timeframe}
                    </Badge>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Main CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-white text-center mb-16 relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.5))]" />
          
          <div className="relative z-10">
            <Badge className="bg-white/20 text-white mb-6 px-6 py-3 text-lg font-bold animate-bounce">
              <Star className="h-5 w-5 mr-2 fill-current" />
              Limited Time: First Event FREE
            </Badge>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Start Streaming in the Next
              <span className="text-yellow-300"> 10 Minutes</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join 500+ activity centers already generating explosive revenue with HiveShare Live. Your first event is completely FREE - no risk, all reward.
            </p>

            {/* Action Options */}
            <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-5xl mx-auto">
              {actionOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative"
                >
                  {option.featured && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-yellow-400 text-yellow-900 px-4 py-1 text-sm font-bold animate-pulse">
                        <Sparkles className="h-4 w-4 mr-1" />
                        MOST POPULAR
                      </Badge>
                    </div>
                  )}
                  
                  <Card className={`h-full transition-all duration-300 ${
                    option.featured 
                      ? 'bg-white/95 border-4 border-yellow-300 scale-110 shadow-2xl' 
                      : 'bg-white/80 border-2 border-white/30 hover:bg-white/90'
                  }`}>
                    <CardContent className="p-6 text-center">
                      <div className={`rounded-2xl p-3 w-fit mx-auto mb-4 ${
                        option.featured 
                          ? 'bg-gradient-to-r from-blue-100 to-purple-100' 
                          : 'bg-gray-100'
                      }`}>
                        <option.icon className={`h-6 w-6 ${
                          option.featured ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{option.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{option.subtitle}</p>
                      
                      <ul className="space-y-2 mb-6">
                        {option.benefits.map((benefit, benefitIndex) => (
                          <li key={benefitIndex} className="flex items-center text-xs text-gray-600">
                            <CheckCircle2 className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className={`w-full font-bold ${
                          option.featured
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                      >
                        {option.buttonText}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Guarantees */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-3xl mx-auto">
              <h3 className="text-xl font-bold mb-4 flex items-center justify-center">
                <Shield className="h-6 w-6 mr-2" />
                100% Risk-Free Guarantee
              </h3>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                {guarantees.map((guarantee, index) => (
                  <div key={index} className="text-blue-100">
                    {guarantee}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contact Options */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Need to Talk to Someone Right Now?
          </h3>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-8 py-4 text-lg font-semibold"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call (555) 123-STREAM
            </Button>
            
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-green-200 text-green-700 hover:bg-green-50 px-8 py-4 text-lg font-semibold"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              Live Chat Support
            </Button>
          </div>
          
          <p className="text-gray-500 text-sm">
            📞 Available 24/7 for immediate assistance • 💬 Average response time: 30 seconds
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamCTA;